import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Shield, Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/Brand";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const googleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const formatDetail = (d) => {
  if (d == null) return "Login failed. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
};

const AdminLogin = () => {
  const navigate = useNavigate();
  const { user, loading, setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate(isAdminRole(user) ? "/admin" : "/dashboard");
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) { toast.error("Enter your email and password."); return; }
    setBusy(true);
    try {
      const res = await api.post("/auth/admin-login", { email: email.trim(), password });
      setUser(res.data);
      toast.success("Welcome back!");
      navigate("/admin", { state: { user: res.data } });
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <header className="h-24 flex items-center px-5 sm:px-8">
        <BrandLogo testId="adminlogin-logo" />
      </header>

      <div className="flex-1 flex items-center justify-center px-5 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <h1 className="font-heading font-extrabold text-2xl text-slate-900">Admin sign in</h1>
          <p className="font-body text-sm text-slate-500 mt-2">Sign in to generate and manage access codes.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email" className="font-heading font-bold text-slate-800">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@rightwaay.com" className="mt-1.5 rounded-xl"
                data-testid="admin-email-input" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="pwd" className="font-heading font-bold text-slate-800">Password</Label>
                <Link to="/forgot-password" className="font-body text-xs text-primary font-semibold hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input id="pwd" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Your admin password" className="mt-1.5 rounded-xl"
                data-testid="admin-password-input" />
            </div>
            <Button type="submit" disabled={busy} data-testid="admin-login-submit"
              className="w-full rounded-xl h-12 font-heading font-bold transition-transform hover:scale-[1.01] active:scale-[0.99]">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Sign in
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="font-body text-xs text-slate-400">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <Button variant="outline" onClick={googleLogin} data-testid="admin-google-btn"
            className="w-full rounded-xl h-11 font-heading font-bold">
            Sign in with Google
          </Button>

          <p className="font-body text-xs text-slate-400 mt-6 text-center">
            Taking the assessment? <Link to="/access" className="text-primary font-semibold">Use your access code</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminLogin;
